import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CommentWithContentContext } from 'shared/types/contentTypes';
import { EllipsisVertical } from 'lucide-react';
import ResonateButton from '../../ResonateButton/ResonateButton';
import ContributeButton from '../../ContributeButton/ContributeButton';
import { renderCommentWithMentions } from '../../../utils/textProcessor';


interface CommentCardProps {
  comment: CommentWithContentContext;
  onLike: (commentId: string, isLiked: boolean, contentId?: string) => Promise<void>;
  onDelete?: (commentId: string, contentId?: string) => Promise<void>;
  onEdit?: (commentId: string, text: string, contentId?: string) => Promise<void>;
  onStartAddingReply?: (commentId: string) => void;
  currentUserId?: string;
  nestingLevel?: number;
  showReplies?: boolean;
  isLastInThread?: boolean;
  showContentContext?: boolean;
  viewingContext?: {
    type: 'artist' | 'album' | 'track' | 'dashboard' | 'contentCard' | 'community';
    entityId?: string;
    entityTitle?: string;
  };
  /** Use 'response' mode for gestures (Respond instead of Contribute) */
  responseMode?: boolean;
  /** Use text-first layout (text content first, author name as subtle metadata below) */
  textFirstLayout?: boolean;
  /** Always show author attribution (name under comment, "You" for own comments) - useful for conversation threads */
  showAuthorAttribution?: boolean;
}

const CommentCard: React.FC<CommentCardProps> = ({
  comment,
  onLike,
  onDelete,
  onEdit,
  onStartAddingReply,
  currentUserId,
  nestingLevel = 0,
  // Used by parent component to control reply visibility
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  showReplies = true,
  isLastInThread = false,
  showContentContext,
  viewingContext,
  responseMode = false,
  textFirstLayout = false,
  showAuthorAttribution = false,
}) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.text);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showActions, setShowActions] = useState(false);
  
  const isAuthor = currentUserId === comment.userId;
  const isDeleted = comment.isDeleted === true;
  const [isResonated, setIsResonated] = useState(comment.reactions?.userReaction === 'resonate');
  const [resonanceCount, setResonanceCount] = useState(comment.reactions?.resonates || 0);
  const [justActivated, setJustActivated] = useState(false);
  
  // Handle starting to add a perspective (reply)
  const handleStartAddingPerspective = () => {
    if (onStartAddingReply) {
      onStartAddingReply(comment.commentId);
    }
  };
  
  // Handle resonate (like/unlike)
  const handleResonate = async () => {
    const newIsResonated = !isResonated;
    
    // Optimistic update - immediately update UI
    setIsResonated(newIsResonated);
    setResonanceCount((prev: number) => newIsResonated ? prev + 1 : Math.max(0, prev - 1));
    
    // Trigger activation pop if resonating (not un-resonating)
    if (newIsResonated) {
      setJustActivated(true);
      setTimeout(() => setJustActivated(false), 300);
    }
    
    try {
      await onLike(comment.commentId, newIsResonated, comment.contentContext.contentId);
    } catch (error) {
      console.error('Failed to resonate with comment:', error);
      // Rollback optimistic update on error
      setIsResonated(!newIsResonated);
      setResonanceCount((prev: number) => newIsResonated ? Math.max(0, prev - 1) : prev + 1);
      setJustActivated(false);
    }
  };
  
  // Handle delete
  const handleDelete = async () => {
    if (onDelete) {
      setIsDeleting(true);
      try {
        await onDelete(comment.commentId, comment.contentContext.contentId);
      } catch (error) {
        console.error('Failed to delete comment:', error);
        setIsDeleting(false);
      }
    }
  };
  
  // Handle view user profile
  const handleViewUser = () => {
    navigate(`/u/${comment.userId}`);
    setShowActions(false);
  };
  
  // Handle edit
  const handleEdit = async () => {
    if (editText.trim() === '') return;
    
    if (onEdit) {
      try {
        await onEdit(comment.commentId, editText, comment.contentContext.contentId);
        setIsEditing(false);
        setShowActions(false);
      } catch (error) {
        console.error('Failed to edit comment:', error);
      }
    } else {
      setIsEditing(false);
      setShowActions(false);
    }
  };
  
  // Render edit form
  const renderEditForm = () => (
    <div className="mt-3">
      <textarea
        value={editText}
        onChange={(e) => setEditText(e.target.value)}
        className="w-full px-4 py-3 border border-gray-200/50 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-coral-pink/20 focus:border-coral-pink/30 bg-gray-50/30 text-base"
        rows={3}
      />
      <div className="flex justify-end gap-3 mt-3">
        <button
          onClick={() => {
            setIsEditing(false);
            setShowActions(false);
          }}
          className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors duration-200"
        >
          Cancel
        </button>
        <button
          onClick={handleEdit}
          className="px-6 py-2 text-sm font-medium bg-gradient-to-r from-coral-pink to-coral-orange text-white rounded-full hover:shadow-lg hover:scale-105 transition-all duration-300"
        >
          Save
        </button>
      </div>
    </div>
  );
  
  // Render content context (only for references to other content)
  const renderContentContext = () => {
    const contentType = comment.contentContext.contentType;
    const contentTitle = comment.contentContext.contentTitle;

    // Check if we should show aligned contributor attribution
    // Only show if we have both userId AND displayName (fallback if displayName missing)
    const showAlignedAttribution = comment.sourceAlignedUserId &&
                                    comment.sourceAlignedUserDisplayName &&
                                    comment.sourceAlignedUserId !== currentUserId;

    // If showContentContext is explicitly false, still show attribution for aligned users
    if (showContentContext === false) {
      if (showAlignedAttribution) {
        return (
          <div className="text-sm text-gray-500 mb-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/u/${comment.sourceAlignedUserId}`);
              }}
              className="text-coral-orange hover:underline"
            >
              {comment.sourceAlignedUserDisplayName}
            </button>
          </div>
        );
      }
      return <div className="py-0"></div>;
    }
    
    // Helper function to get context-aware title
    const getContextAwareTitle = () => {
      // If we have viewing context, adjust the display
      if (viewingContext) {
        // If viewing the same entity as the comment, no context needed
        // Compare with comment's entityId (the entity the comment is about)
        if (viewingContext.entityId === comment.entityId) {
          return null;
        }
        
        // If viewing an album and comment is on a track within that album
        if (viewingContext.type === 'album' && comment.entityType === 'musicTrack' && contentTitle) {
          // Extract just the track name if the title has "from" pattern
          const fromMatch = contentTitle.match(/^(.+)\s+from\s+(.+)$/);
          if (fromMatch) {
            return fromMatch[1]; // Return just the track name
          }
        }
      }
      
      // Default: return the full title
      return contentTitle;
    };

    // Build navigation URLs for each entity type
    const buildTrackUrl = (): string | null => {
      const { artistId, albumId, trackId } = comment.contentContext;
      if (artistId && albumId && trackId) {
        return `/artist/${artistId}?view=discography&album=${albumId}&track=${trackId}`;
      }
      return null;
    };

    const buildAlbumUrl = (): string | null => {
      const { artistId, albumId } = comment.contentContext;
      if (artistId && albumId) {
        return `/artist/${artistId}?view=discography&album=${albumId}`;
      }
      return null;
    };

    const buildVideoUrl = (): string | null => {
      const { artistId, videoId } = comment.contentContext;
      if (artistId && videoId) {
        return `/artist/${artistId}?view=discography&video=${videoId}`;
      }
      return null;
    };

    const buildArtistUrl = (): string | null => {
      const { artistId } = comment.contentContext;
      if (artistId) {
        return `/artist/${artistId}?view=discography`;
      }
      return null;
    };

    // Clickable entity name component
    const ClickableEntityName: React.FC<{ name: string; url: string | null }> = ({ name, url }) => {
      if (url) {
        return (
          <Link
            to={url}
            onClick={(e) => e.stopPropagation()}
            className="font-normal text-coral-pink hover:underline decoration-coral-pink"
          >
            {name}
          </Link>
        );
      }
      return <span className="font-normal text-coral-pink">{name}</span>;
    };

    // Helper function to render title with proper highlighting and navigation
    const renderHighlightedTitle = (title: string) => {
      const { entityType } = comment.contentContext;

      // Check if title has the pattern "Track Name from Album Name"
      const fromMatch = title.match(/^(.+)\s+from\s+(.+)$/);
      if (fromMatch) {
        const [, trackName, albumName] = fromMatch;
        return (
          <>
            <ClickableEntityName name={trackName} url={buildTrackUrl()} />
            <span className="font-light"> from </span>
            <ClickableEntityName name={albumName} url={buildAlbumUrl()} />
          </>
        );
      }

      // Check if title has the pattern "Video Title by Artist Name"
      const byMatch = title.match(/^(.+)\s+by\s+(.+)$/);
      if (byMatch) {
        const [, videoName, artistName] = byMatch;
        return (
          <>
            <ClickableEntityName name={videoName} url={buildVideoUrl()} />
            <span className="font-light"> by </span>
            <ClickableEntityName name={artistName} url={buildArtistUrl()} />
          </>
        );
      }

      // No pattern match - determine URL based on entity type
      let url: string | null = null;
      if (entityType === 'musicTrack') {
        url = buildTrackUrl();
      } else if (entityType === 'musicRelease') {
        url = buildAlbumUrl();
      } else if (entityType === 'youtubeVideo') {
        url = buildVideoUrl();
      }

      return <ClickableEntityName name={title} url={url} />;
    };
    
    const displayTitle = getContextAwareTitle();

    // If no title to display (same context), still show attribution if present
    if (!displayTitle) {
      if (showAlignedAttribution) {
        return (
          <div className="text-sm text-gray-500 mb-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/u/${comment.sourceAlignedUserId}`);
              }}
              className="text-coral-orange hover:underline"
            >
              {comment.sourceAlignedUserDisplayName}
            </button>
          </div>
        );
      }
      return <div className="py-0"></div>;
    }
    
    // Clean the title
    const cleanTitle = displayTitle.replace(/^New Release: /, '');
    
    // If showContentContext is explicitly true or we have a title to show
    // For fallback (undefined), show context for entity types that need it (tracks, albums, videos)
    // but not for general artist discussions where context would be redundant
    const entityTypesRequiringContext = ['musicTrack', 'musicRelease', 'youtubeVideo'];
    const shouldShowByEntityType = comment.entityType && entityTypesRequiringContext.includes(comment.entityType);
    if (showContentContext === true || (showContentContext === undefined && (contentType !== 'discussion' || shouldShowByEntityType))) {
      return (
        <div className="text-sm text-gray-500 mb-3">
          <span className="font-light">{nestingLevel > 0 ? '' : 'Contribution '}</span>
          {showAlignedAttribution && (
            <>
              <span className="font-light">by </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/u/${comment.sourceAlignedUserId}`);
                }}
                className="text-coral-orange hover:underline"
              >
                {comment.sourceAlignedUserDisplayName || `${comment.sourceAlignedUserId?.slice(0, 8)}...`}
              </button>
              <span className="font-light"> </span>
            </>
          )}
          <span className="font-light">on </span>
          {renderHighlightedTitle(cleanTitle)}
        </div>
      );
    }
    
    return <div className="py-0"></div>;
  };
  
  const renderSoundwaveButton = (isNested: boolean = false) => {
    return (
      <ResonateButton
        isResonated={isResonated}
        onClick={handleResonate}
        showText={!isNested}
        size={isNested ? 'secondary' : 'primary'}
        isNested={isNested}
        count={isNested ? undefined : resonanceCount}
        disabled={!currentUserId}
        showActivationPop={justActivated}
        testId="comment-resonate-button"
        commentId={comment.commentId}
      />
    );
  };

  return (
    <div 
      className={`${nestingLevel > 0 ? `pl-0 pb-3 relative nested-comment ${isLastInThread ? 'last-in-thread' : ''}` : ''}`}
      data-testid="comment-container"
      data-comment-id={comment.commentId}
    >
      {nestingLevel > 0 && (
        <style>{`
          .nested-comment {
            position: relative;
          }

          .nested-comment::before {
            content: '';
            position: absolute;
            top: 2px;
            left: 0px;
            width: 16px;
            height: 12px;
            border-bottom: 1px solid #8D86C9;
            border-left: 1px solid #8D86C9;
            border-bottom-left-radius: 6px;
          }

          /* Add a special style for the last comment in the thread */
          .nested-comment.last-in-thread::after {
            content: '';
            position: absolute;
            top: 12px; /* Position it right below the connection line */
            bottom: 0;
            left: 0px;
            width: 1px;
            background-color: #faf9f7; /* Hide the trunk line below the last comment */
            z-index: 1;
          }
        `}</style>
      )}
      {/* Container with proper spacing for three-dot menu */}
      <div className="relative pr-8">
        {/* Three-dot action menu - positioned outside text area */}
        {!isDeleted && (
          <div className="absolute -top-1 -right-6">
            <button 
              onClick={() => setShowActions(!showActions)}
              className="text-gray-400/60 hover:text-gray-600 focus:outline-none p-1"
              aria-label="Comment options"
            >
              <EllipsisVertical size={16} />
            </button>
            
            {/* Action menu dropdown */}
            {showActions && (
              <>
                <div 
                  className="fixed inset-0 w-full h-full z-40" 
                  onClick={() => setShowActions(false)}
                ></div>
                <div className="absolute right-0 top-6 bg-white shadow-md rounded-md py-1 z-50 w-28">
                  <button
                    onClick={handleViewUser}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 transition-colors duration-200"
                  >
                    View User
                  </button>
                  {isAuthor && (
                    <>
                      <button
                        onClick={() => {
                          setIsEditing(true);
                          setShowActions(false);
                        }}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 transition-colors duration-200"
                      >
                        Edit
                      </button>
                      {onDelete && !isDeleting && (
                        <button
                          onClick={handleDelete}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 text-red-500 transition-colors duration-200"
                        >
                          Delete
                        </button>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        
        {nestingLevel > 0 && !isDeleted && !isEditing ? (
          // Reply layout (nestingLevel > 0) with soundwave on the left
          <div className="flex items-start ml-4">

            {/* Soundwave icon on the left */}
            <div className="flex-shrink-0 pt-1 min-w-[30px]">
              {renderSoundwaveButton(true)}
            </div>

            {/* Comment text content - inline attribution for conversation threads */}
            <div className="flex-1">
              <div className="text-gray-800 text-base font-light leading-relaxed">
                {/* Inline author prefix for conversation threads */}
                {(() => {
                  const isOwnComment = comment.userId === currentUserId;

                  if (showAuthorAttribution) {
                    const authorName = isOwnComment ? 'You' : (comment.displayName || comment.sourceAlignedUserDisplayName);
                    if (authorName) {
                      return (
                        <>
                          {isOwnComment ? (
                            <span className="text-gray-400 text-sm">{authorName}</span>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/u/${comment.userId}`);
                              }}
                              className="text-gray-400 text-sm hover:text-coral-pink hover:underline"
                            >
                              {authorName}
                            </button>
                          )}
                          <span className="text-gray-300 text-sm mx-1.5">·</span>
                        </>
                      );
                    }
                  }

                  // Default: only show aligned user attribution as inline prefix
                  if (comment.sourceAlignedUserId && comment.sourceAlignedUserDisplayName && comment.sourceAlignedUserId !== currentUserId) {
                    return (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/u/${comment.sourceAlignedUserId}`);
                          }}
                          className="text-gray-400 text-sm hover:text-coral-pink hover:underline"
                        >
                          {comment.sourceAlignedUserDisplayName}
                        </button>
                        <span className="text-gray-300 text-sm mx-1.5">·</span>
                      </>
                    );
                  }

                  return null;
                })()}
                {renderCommentWithMentions(comment.text, comment.mentionMetadata)}
              </div>
            </div>
          </div>
        ) : (
          // Original layout for top-level comments
          <>
            {/* Main comment content */}
            <div className="mb-3">
              {/* Content reference - only show if NOT using text-first layout */}
              {!textFirstLayout && renderContentContext()}

              {/* Comment text content or edit form */}
              {isEditing ? (
                renderEditForm()
              ) : (
                <div className={`text-gray-800 text-base font-light leading-relaxed ${isDeleted ? 'italic text-gray-400' : ''}`}>
                  {renderCommentWithMentions(comment.text, comment.mentionMetadata)}
                </div>
              )}

              {/* Attribution shown below text as subtle metadata */}
              {(() => {
                // Determine what attribution to show
                const isOwnComment = comment.userId === currentUserId;

                // For showAuthorAttribution mode (conversation threads), always show author
                if (showAuthorAttribution) {
                  const authorName = isOwnComment ? 'You' : (comment.displayName || comment.sourceAlignedUserDisplayName);
                  if (authorName) {
                    return (
                      <div className="text-xs mt-1">
                        {isOwnComment ? (
                          <span className="text-gray-400">{authorName}</span>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/u/${comment.userId}`);
                            }}
                            className="text-coral-orange hover:text-coral-pink hover:underline"
                          >
                            {authorName}
                          </button>
                        )}
                      </div>
                    );
                  }
                }

                // For textFirstLayout without showAuthorAttribution, only show aligned user (existing behavior)
                if (textFirstLayout && comment.sourceAlignedUserId && comment.sourceAlignedUserDisplayName && comment.sourceAlignedUserId !== currentUserId) {
                  return (
                    <div className="text-xs mt-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/u/${comment.sourceAlignedUserId}`);
                        }}
                        className="text-coral-orange hover:text-coral-pink hover:underline"
                      >
                        {comment.sourceAlignedUserDisplayName}
                      </button>
                    </div>
                  );
                }

                return null;
              })()}
            </div>

            {/* Interaction elements */}
            {!isDeleted && !isEditing && (
              <div className="flex items-start mt-3 text-sm text-gray-400/60">
                {/* Resonate button - moved to the start of text content */}
                <div className="flex mr-3">
                  {renderSoundwaveButton(false)}
                </div>

                {/* Add Perspective button - only for top-level comments */}
                {currentUserId && nestingLevel === 0 && onStartAddingReply && (
                  <div className="">
                    <ContributeButton
                      onClick={handleStartAddingPerspective}
                      textMode={responseMode ? 'respond' : 'contribute'}
                      size="primary"
                      testId="comment-add-perspective-button"
                      commentId={comment.commentId}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CommentCard;